import React, { FC, useEffect } from 'react';
import { Row, Col, Select } from 'antd';
import { useTranslation } from 'react-i18next';

import { ContentFilterProps } from 'hoc/withFilter';
import { UserPermissionLocalesKey } from 'elements/FormikSelectUserPermissions/types';
import { StyledFilterFormSearch } from 'pages/Wim/Grid/styles';
import { UserPermission, UserStatusFilter } from 'store/user/types';
import adminSelectors from 'store/admin/selectors';
import { Group } from 'store/admin/usergroups/types';
import { SelectValue } from 'antd/lib/select';
import { useAppSelector } from 'hooks/store-hooks';
import { StyledSelect } from './styles';
import { UsersFilterFormValues } from '../../types';

const { Option } = Select;

export const FilterPanel: FC<ContentFilterProps<UsersFilterFormValues>> = ({ filterManager }) => {
  const { t } = useTranslation();
  const { handleFilterChange, state, onFilterSubmitClick } = filterManager;

  const onChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFilterChange('search', event.currentTarget.value);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      onFilterSubmitClick();
    }
  };

  const onStatusChange = (value?: UserStatusFilter.ACTIVE | UserStatusFilter.INACTIVE | UserStatusFilter.ALL) => {
    handleFilterChange('status', value);
  };

  const onUserGroupChange = (value: SelectValue) => {
    const v = (value ?? []) as string[];

    handleFilterChange('userGroup', v);
  };

  const onPermissionsChange = (value: SelectValue) => {
    const v = (value ?? []) as string[];

    handleFilterChange('permissions', v);
  };

  const userGroups = useAppSelector(adminSelectors.usergroups.getGroups);

  return (
    <Row>
      <Col span={9}>
        <StyledFilterFormSearch
          id="search"
          name="search"
          placeholder={t('common.search')}
          onChange={onChange}
          value={state.search}
          onKeyDown={onKeyDown}
        />
        <StyledSelect
          value={state.permissions}
          maxTagCount={1}
          allowClear
          mode="multiple"
          placeholder={t('filter.permissions')}
          onChange={onPermissionsChange}
          showSearch
        >
          <Option value="all">{t('filter.selectAll')}</Option>
          {Object.keys(UserPermission).map((permission) => (
            <Option key={`user-permission-${permission}`} value={`wim:${permission.toLowerCase()}`}>
              {t(`user.permissions.${permission.toLowerCase() as UserPermissionLocalesKey}` as const)}
            </Option>
          ))}
        </StyledSelect>
      </Col>

      <Col span={9}>
        <Select<UserStatusFilter.ALL | UserStatusFilter.ACTIVE | UserStatusFilter.INACTIVE>
          style={{ width: '100%' }}
          placeholder={t('common.status') || 'User activity'}
          allowClear
          value={state.status}
          onChange={onStatusChange}
          options={[
            { value: UserStatusFilter.ALL, label: t('user.status.ALL') || 'All' },
            { value: UserStatusFilter.ACTIVE, label: t('user.status.ACTIVE') || 'Active' },
            { value: UserStatusFilter.INACTIVE, label: t('user.status.INACTIVE') || 'Non-Active' },
          ]}
        />
        <StyledSelect
          value={state.userGroup}
          maxTagCount={1}
          allowClear
          mode="multiple"
          placeholder={t('filter.userGroups')}
          onChange={onUserGroupChange}
          showSearch
        >
          <Option value="all">{t('filter.selectAll')}</Option>
          {userGroups?.map((group: Group) => (
            <Option key={group.uid} value={group.uid}>
              <div>{group.name}</div>
            </Option>
          ))}
        </StyledSelect>
      </Col>
    </Row>
  );
};
